import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import { AgentSearchError, isAbortError, type AgentSearchClient } from '../../core/client'
import { applySettled, isExactTotal, recordSettled, type SettledTotals } from '../../core/totals'
import type { ConfigResponse, ResultsEventPayload, SearchResponse } from '../../core/types'
import { buildSearchQuery, clampPage, clampQuery, readSearchParams } from '../../core/url'

export type SearchStatus = 'idle' | 'loading' | 'success' | 'error'

type SearchOutcome = 'applied' | 'failed' | 'aborted'

/** Redirects from an empty page beyond the last, per user action; the last one goes to page 1. */
const MAX_REDIRECTS = 2

export interface UseSearchOptions {
    client: AgentSearchClient | null
    onResults?: (payload: ResultsEventPayload) => void
    onPageChange?: () => void
}

/**
 * Search state of the page. Every user action (search, page click, history navigation) sends one `/search` request,
 * every request counts against the rate limit of the API; a page beyond the real end adds at most MAX_REDIRECTS.
 */
export function useSearch(options: UseSearchOptions) {
    const status = ref<SearchStatus>('idle')
    const query = ref('')
    const page = ref(1)
    const response = shallowRef<SearchResponse | null>(null)
    const error = shallowRef<AgentSearchError | null>(null)
    const config = shallowRef<ConfigResponse | null>(null)
    const locationSearch = ref('')
    /** The shown total is exact (the last page was seen); otherwise Google's estimate. */
    const exactTotal = ref(false)

    let controller: AbortController | null = null
    const configController = new AbortController()
    // what the pages of one query proved about its total, kept while paging within it
    let settledQuery = ''
    let settled: SettledTotals = new Map()

    function abortPending(): void {
        controller?.abort()
        controller = null
    }

    /**
     * Runs a search; resolves whether its result was applied, the request failed, or a newer search aborted it.
     * `onShown` runs as soon as the result or the error is shown.
     */
    async function search(q: string, p: number, onShown?: () => void, redirects = 0): Promise<SearchOutcome> {
        if (!options.client) {
            return 'failed'
        }

        abortPending()
        const current = new AbortController()
        controller = current

        query.value = q
        page.value = p
        status.value = 'loading'

        if (settledQuery !== q) {
            settledQuery = q
            settled = new Map()
        }

        try {
            const result = await options.client.search({ q, page: p }, current.signal)

            if (current.signal.aborted) {
                return 'aborted'
            }

            recordSettled(result, settled)

            // an empty page beyond the last (old link, page after Google's estimate or the max_results cap):
            // the last page known to exist instead, on the last allowed redirect page 1
            if (redirects < MAX_REDIRECTS && isBeyondLast(result, p)) {
                const target = redirects === MAX_REDIRECTS - 1 ? 1 : lastPageBefore(result, p, settled)

                replaceUrl(q, target)

                return await search(q, target, onShown, redirects + 1)
            }

            response.value = applySettled(result, settled)
            exactTotal.value = isExactTotal(result, settled)
            error.value = result.success ? null : new AgentSearchError('SEARCH_FAILED', 200, 'every block failed')
            status.value = result.success ? 'success' : 'error'
            options.onResults?.(toPayload(q, p, response.value, exactTotal.value))
            onShown?.()

            return 'applied'
        } catch (caught) {
            if (isAbortError(caught) || current.signal.aborted) {
                return 'aborted'
            }

            response.value = null
            exactTotal.value = false
            error.value = caught instanceof AgentSearchError ? caught : new AgentSearchError('NETWORK', 0, String(caught))
            status.value = 'error'
            options.onResults?.({ query: q, page: p, success: false, error: error.value.code, total: 0, exact: false, blocks: [] })
            onShown?.()

            return 'failed'
        } finally {
            if (controller === current) {
                controller = null
            }
        }
    }

    function reset(): void {
        abortPending()
        exactTotal.value = false
        query.value = ''
        page.value = 1
        response.value = null
        error.value = null
        status.value = 'idle'
    }

    function replaceUrl(q: string, p: number): void {
        const next = buildSearchQuery(window.location.search, { q, page: p })

        window.history.replaceState(window.history.state, '', `${window.location.pathname}${next}${window.location.hash}`)
        locationSearch.value = window.location.search
    }

    function pushUrl(q: string, p: number): void {
        const next = buildSearchQuery(window.location.search, { q, page: p })

        // the same search again (e.g. submitting the unchanged query) refreshes without a duplicate history entry
        if (next === window.location.search) {
            return
        }

        window.history.pushState(null, '', `${window.location.pathname}${next}${window.location.hash}`)
        locationSearch.value = window.location.search
    }

    function submit(q: string): void {
        const trimmed = clampQuery(q)

        if (!trimmed) {
            return
        }

        pushUrl(trimmed, 1)
        void search(trimmed, 1)
    }

    function goToPage(target: number): void {
        if (!query.value) {
            return
        }

        const p = clampPage(target)
        pushUrl(query.value, p)
        // also after a failure: the error text replaces the results, so focus and scroll go to it
        void search(query.value, p, options.onPageChange)
    }

    function syncFromUrl(): void {
        locationSearch.value = window.location.search
        const params = readSearchParams(window.location.search)

        if (params.q) {
            void search(params.q, params.page)
        } else {
            reset()
        }
    }

    function onPopState(): void {
        // popstate also fires for hash-only navigation; the same q and page must not cost another Google query
        const params = readSearchParams(window.location.search)

        if (status.value !== 'idle' && params.q === query.value && params.page === page.value) {
            locationSearch.value = window.location.search

            return
        }

        syncFromUrl()
    }

    onMounted(() => {
        if (!options.client) {
            status.value = 'error'

            return
        }

        window.addEventListener('popstate', onPopState)

        void options.client
            .config(configController.signal)
            .then((loaded) => {
                config.value = loaded
            })
            .catch(() => {
                // the config only carries phase 2 flags; a search works without it
            })

        syncFromUrl()
    })

    onBeforeUnmount(() => {
        window.removeEventListener('popstate', onPopState)
        configController.abort()
        abortPending()
    })

    return { status, query, page, response, error, config, locationSearch, exactTotal, submit, goToPage }
}

function toPayload(q: string, p: number, result: SearchResponse, exact: boolean): ResultsEventPayload {
    return {
        query: q,
        page: p,
        success: result.success,
        ...(result.success ? {} : { error: 'SEARCH_FAILED' }),
        total: result.blocks.reduce((sum, block) => sum + (block.success ? block.total : 0), 0),
        exact: result.success && exact,
        blocks: result.blocks.map((block) => ({
            type: block.type,
            success: block.success,
            total: block.total,
            count: block.items.length,
        })),
    }
}

/** An empty page after the first: every paged block succeeded without items. */
function isBeyondLast(result: SearchResponse, p: number): boolean {
    if (!result.success || p <= 1) {
        return false
    }

    const paged = result.blocks.filter((block) => block.success && block.pageCount !== undefined)

    return paged.length > 0 && paged.every((block) => block.items.length === 0)
}

/** The last page known to exist before the empty page `p`: settled, else the estimated last page; at least 1. */
function lastPageBefore(result: SearchResponse, p: number, known: SettledTotals): number {
    const paged = result.blocks.filter((block) => block.success && block.pageCount !== undefined)
    const last = Math.max(...paged.map((block) => known.get(block.type)?.pageCount ?? block.pageCount!))

    return Math.max(1, Math.min(p - 1, last))
}
